import { useApiCall } from "@/app/api/apiCall";
import { PaginatedResponse } from "@/app/api/types";
import AppLayout from "@/app/components/AppLayout";
import Filters from "@/app/components/filters/Filters";
import { LoadingFilters } from "@/app/components/LoadingFilters";
import { LoadingPagination } from "@/app/components/LoadingPagination";
import { LoadingSearch } from "@/app/components/LoadingSearch";
import { LoadingTable } from "@/app/components/LoadingTable";
import { Pagination } from "@/app/components/Pagination";
import {
  IPrivateContext,
  PrivateContext,
} from "@/app/components/PrivateContextLoader";
import { SearchBar } from "@/app/components/SearchBar";
import { SelectPageSize } from "@/app/components/SelectPageSize";
import { useApiContext } from "@/app/contexts/ApiContext";
import { buildColumns } from "@/app/fields/buildColumns";
import { parseSearchTerm } from "@/app/fields/parseSearchTerm";
import { Entity } from "@/app/fields/types";
import { INavLinks, NavContext } from "@/app/NavContext";
import { isSortingState } from "@/app/utils";
import { Box, Button } from "@chakra-ui/react";
import { DataGrid } from "@saas-ui-pro/react";
import { Sidebar, SidebarSection } from "@saas-ui/react";
import { SortingState, Updater } from "@tanstack/table-core";
import { useContext, useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router";
import { useNavigate } from "react-router-dom";

const parseSort = (searchParams: URLSearchParams): SortingState => {
  const raw = searchParams.get("sort");
  if (!raw) return [];
  if (raw.startsWith("-")) {
    return [
      {
        desc: true,
        id: raw.slice(1),
      },
    ];
  }
  return [
    {
      desc: false,
      id: raw,
    },
  ];
};

// All state is saved in the url
export default function TablePage<T extends Entity>({
  exportBtn,
  searchPlaceholder,
  className,
}: {
  exportBtn?: (selectedRows: T[]) => React.ReactNode;
  searchPlaceholder: string;
  className?: string;
}) {
  const { listData, getFieldOptions, createEntry } = useApiContext();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname as keyof INavLinks;

  const [searchParams, setSearchParams] = useSearchParams();

  const { updateNavLink } = useContext(NavContext);
  const { fields } = useContext(PrivateContext) as IPrivateContext<T>;
  const handle = useApiCall();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dataResponse, setDataResponse] = useState<
    PaginatedResponse<T> | undefined
  >();

  const onClearAllFilters = () => {
    setSearchParams((prev) => {
      const updated = new URLSearchParams();
      ["sort", "page_size", "search", "hide_cols"].forEach((param) => {
        const value = prev.get(param);
        if (value !== null) {
          updated.set(param, value);
        }
      });
      return updated;
    });
  };

  const onSort = (sort: Updater<SortingState>) => {
    const updated: SortingState = isSortingState(sort)
      ? sort
      : sort(parseSort(searchParams));
    setSearchParams((prev) => {
      prev.delete("sort");
      updated.forEach(({ id, desc }) =>
        prev.set("sort", `${desc ? "-" : ""}${id}`)
      );
      prev.delete("page");
      return prev;
    });
  };

  const onPageSizeChange = (page_size: number) => {
    setSearchParams((prev) => {
      prev.set("page_size", `${page_size}`);
      prev.delete("page");
      return prev;
    });
  };

  const onPageChange = (page: number) => {
    setSearchParams((prev) => {
      prev.set("page", `${page}`);
      return prev;
    });
  };

  const onSearch = (value?: string) => {
    setSearchParams((prev) => {
      if (value !== undefined) {
        prev.set("search", value);
      } else {
        prev.delete("search");
      }
      prev.delete("page");
      return prev;
    });
  };

  const fetchData = async (params: URLSearchParams) => {
    setIsLoading(true);
    await handle(
      () => listData(params),
      (response) => {
        setDataResponse(response);
        setIsLoading(false);
      }
    );
  };

  useEffect(() => {
    updateNavLink(pathname, `${pathname}?${searchParams.toString()}`);
    const copy = new URLSearchParams(searchParams.toString());
    copy.delete("hide_cols");
    fetchData(copy);
  }, [searchParams]);

  const [selectedEntities, setSelectedEntities] = useState<T[]>([]);

  return (
    <AppLayout
      navbarContent={
        !!fields ? (
          <SearchBar
            placeholder={searchPlaceholder}
            defaultValue={parseSearchTerm(searchParams)}
            onSubmit={onSearch}
          />
        ) : (
          <LoadingSearch />
        )
      }
      sidebar={
        <Sidebar
          toggleBreakpoint="sm"
          className="grow overflow-scroll no-scrollbar"
        >
          <SidebarSection className="grow">
            {!fields ? (
              <>
                <LoadingFilters />
              </>
            ) : (
              <>
                <Filters
                  isDisabled={isLoading}
                  getFieldOptions={getFieldOptions}
                  fields={fields}
                  onClearAllFilters={onClearAllFilters}
                />
              </>
            )}
          </SidebarSection>
        </Sidebar>
      }
      footer={
        isLoading || !dataResponse || !fields ? (
          <LoadingPagination />
        ) : (
          <div className="grid grid-cols-3 w-full">
            <SelectPageSize
              pageSize={dataResponse.page_size}
              onPageSizeChange={onPageSizeChange}
            />
            <Pagination
              onPageChange={onPageChange}
              page={dataResponse.page}
              page_size={dataResponse.page_size}
              count={dataResponse.count}
            />
            <div className="flex justify-end items-center gap-x-4">
              {!!exportBtn && exportBtn(selectedEntities)}
              {!!createEntry && (
                <Link to={`${pathname}/new`}>
                  <Button variant="primary">Add new</Button>
                </Link>
              )}
            </div>
          </div>
        )
      }
    >
      <Box as="main" flex="1" py="2" px="4" overflowY="auto">
        {isLoading || !dataResponse || !fields ? (
          <LoadingTable />
        ) : (
          <DataGrid<T>
            getRowId={({ id }) => id}
            className={className}
            sx={{
              "& tbody tr:hover": {
                cursor: "pointer",
              },
            }}
            columnResizeEnabled={true}
            isHoverable={true}
            onRowClick={(row) => navigate(`${pathname}/${row.original.id}`)}
            manualSorting={true}
            state={{
              sorting: parseSort(searchParams),
            }}
            onSortingChange={onSort}
            columns={buildColumns(fields)}
            size="sm"
            data={dataResponse.results}
            isSortable
            isSelectable={!!exportBtn}
            onSelectedRowsChange={(selected) =>
              setSelectedEntities(
                dataResponse.results.filter(({ id }) => selected.includes(id))
              )
            }
            pageCount={dataResponse.results.length}
          />
        )}
      </Box>
    </AppLayout>
  );
}
